import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  CheckCircle2,
  Bot,
  MapPin,
  Wallet,
  Sparkles,
  LogIn,
  Inbox,
  Receipt,
  AlertCircle,
  TrendingUp,
  Calendar,
  Users,
  FileText,
  Zap,
  Star,
  Briefcase,
} from "lucide-react";
import TierSelectionDialog, { SubscriptionTier } from "@/components/TierSelectionDialog";
import { AutopilotShowcase } from "@/components/Landing/AutopilotShowcase";
import { IntegrationsGrid } from "@/components/Landing/IntegrationsGrid";
import { INDUSTRIES } from "@/lib/industries";

const Landing = () => {
  const navigate = useNavigate();
  const [showTierDialog, setShowTierDialog] = useState(false);

  const handleTierSelect = (tier: SubscriptionTier) => {
    localStorage.setItem("selectedTier", JSON.stringify({
      id: tier.id,
      name: tier.name,
      price: tier.price,
      priceId: tier.priceId,
      productId: tier.productId,
      selectedAt: new Date().toISOString(),
    }));
    setShowTierDialog(false);
    navigate("/free-trial");
  };

  const handleGetStarted = () => setShowTierDialog(true);

  const agents = [
    { icon: Inbox, name: "Inbox Agent", desc: "Drafts and sends replies in your voice" },
    { icon: Receipt, name: "Invoice Agent", desc: "Bills clients automatically when jobs complete" },
    { icon: AlertCircle, name: "Collections Agent", desc: "Chases overdue invoices politely, then firmly" },
    { icon: Calendar, name: "Booking Agent", desc: "Negotiates appointment times via SMS" },
    { icon: TrendingUp, name: "CFO Agent", desc: "Writes monthly P&L commentary you'd pay $5k/mo for" },
    { icon: Bot, name: "Books Agent", desc: "Categorizes every transaction, reconciles every feed" },
  ];

  const accountingTiles = [
    { name: "A/R Aging Report", desc: "See who owes what, instantly" },
    { name: "Cash Flow Forecast", desc: "Project 90 days ahead" },
    { name: "Mileage Tracker", desc: "Auto-log + IRS-ready exports" },
    { name: "1099 Generator", desc: "File contractor forms in clicks" },
    { name: "Bank Reconciliation", desc: "Match transactions side-by-side" },
    { name: "Chart of Accounts", desc: "Full double-entry, GAAP compliant" },
  ];

  const tiers = [
    { name: "Starter", price: "$29", trial: "3 months FREE", features: ["Unlimited clients", "AutoPilot core", "Mobile app", "Email support"], highlight: false },
    { name: "Professional", price: "$79", trial: "6 months FREE", features: ["Everything in Starter", "AI Consultant", "Relationship Intel", "Advanced reports", "10 team seats"], highlight: true },
    { name: "Business", price: "$199", trial: "1 YEAR FREE", features: ["Everything in Pro", "Unlimited team", "API access", "White-label", "Priority support"], highlight: false },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary p-1.5">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">Detail Pro</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="/features" className="text-muted-foreground hover:text-foreground">Features</a>
            <a href="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</a>
            <a href="/switch-and-save" className="text-muted-foreground hover:text-foreground">Switch & Save</a>
            <a href="/about" className="text-muted-foreground hover:text-foreground">About</a>
            <a href="/contact" className="text-muted-foreground hover:text-foreground">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/auth")}>
              <LogIn className="h-4 w-4 mr-1" /> Sign In
            </Button>
            <Button size="sm" onClick={handleGetStarted}>Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/5" />
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                <Sparkles className="h-3 w-3 mr-1" /> The world's first autonomous business OS
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                The first business that{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">runs itself.</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                Tell AutoPilot "run my business" and it actually does — answers messages, sends invoices, chases payments, books appointments, and keeps your books. 24/7. While you sleep.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" onClick={handleGetStarted} className="group">
                  Start free for up to 1 year
                  <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/schedule-demo")}>
                  Watch 90-second demo
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> No credit card</div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 50+ industries</div>
                <div className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> Migrate in 1 click</div>
              </div>
            </div>
            <div className="lg:pl-8">
              <AutopilotShowcase />
            </div>
          </div>
        </div>
      </section>

      {/* Meet AutoPilot */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="mb-3">Meet AutoPilot</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">7 agents. One brain. Zero busywork.</h2>
            <p className="text-muted-foreground">Each agent runs autonomously with an approval threshold you control. Set it to "ask first" or "full autonomy" — your call.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map((agent) => {
              const Icon = agent.icon;
              return (
                <div key={agent.name} className="rounded-xl border border-border bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all">
                  <div className="rounded-lg bg-primary/10 p-2 w-fit mb-3">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{agent.name}</h3>
                  <p className="text-sm text-muted-foreground">{agent.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Video */}
      <section className="py-16 md:py-20 border-t border-border/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 space-y-3">
            <Badge variant="secondary"><Sparkles className="h-3 w-3 mr-1" /> 90-second tour</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">See it run in 90 seconds</h2>
            <p className="text-muted-foreground">From signup to first invoice — the full loop.</p>
          </div>
          <div className="relative rounded-2xl border border-border overflow-hidden shadow-2xl bg-card aspect-video group cursor-pointer" onClick={handleGetStarted}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-accent/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute -inset-6 bg-primary/30 rounded-full animate-ping" />
                <div className="relative h-20 w-20 rounded-full bg-primary flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <svg className="h-8 w-8 text-primary-foreground ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                </div>
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-xs text-foreground/70">
              <span className="px-2 py-1 rounded bg-background/80 backdrop-blur">Product Tour</span>
              <span className="px-2 py-1 rounded bg-background/80 backdrop-blur">1:30</span>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4">Or skip ahead — start your free trial below.</p>
        </div>
      </section>

      {/* Find customers near you */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-5">
              <Badge variant="secondary"><MapPin className="h-3 w-3 mr-1" /> Prospect Map</Badge>
              <h2 className="text-3xl md:text-4xl font-bold">Find your next 100 customers — on a map.</h2>
              <p className="text-muted-foreground text-lg">
                We pre-load a database of nearby prospects matched to your industry. See them on a map, route the most promising, convert with one tap.
              </p>
              <ul className="space-y-2 text-sm">
                {["Industry-targeted lead database", "Live map with one-tap conversion", "Door-knocking tracker for field crews", "Auto-routes optimized by distance"].map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-border overflow-hidden shadow-2xl bg-card aspect-[4/3] relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10" />
              <div className="absolute inset-0 grid grid-cols-8 grid-rows-6 gap-px opacity-20">
                {Array.from({ length: 48 }).map((_, i) => (
                  <div key={i} className="bg-foreground/10" />
                ))}
              </div>
              {[
                { x: "20%", y: "30%" }, { x: "55%", y: "25%" }, { x: "75%", y: "45%" },
                { x: "30%", y: "60%" }, { x: "60%", y: "70%" }, { x: "45%", y: "40%" },
                { x: "85%", y: "65%" }, { x: "15%", y: "75%" },
              ].map((p, i) => (
                <div key={i} className="absolute" style={{ left: p.x, top: p.y }}>
                  <div className="relative">
                    <div className="absolute -inset-2 bg-primary/30 rounded-full animate-ping" />
                    <MapPin className="relative h-6 w-6 text-primary fill-primary/30" />
                  </div>
                </div>
              ))}
              <div className="absolute bottom-4 left-4 right-4 rounded-lg bg-card/95 backdrop-blur border p-3 shadow-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="font-semibold">42 prospects</span>
                  <span className="text-muted-foreground">within 5 miles</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QuickBooks-class accounting */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="secondary"><Wallet className="h-3 w-3 mr-1" /> Accounting</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 mt-3">QuickBooks-class accounting, included.</h2>
            <p className="text-muted-foreground">Stop paying $90/mo for QuickBooks on top of your CRM. We have it built in — and AutoPilot runs it for you.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {accountingTiles.map((t) => (
              <div key={t.name} className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">{t.name}</h3>
                </div>
                <p className="text-sm text-muted-foreground pl-6">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <IntegrationsGrid />
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Badge variant="secondary"><Briefcase className="h-3 w-3 mr-1" /> 50+ Industries</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 mt-3">Built for your industry.</h2>
            <p className="text-muted-foreground">Pre-loaded service templates, lead databases, and automations specific to what you do.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {INDUSTRIES.map((i) => (
              <span key={i.slug} className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground/80 hover:border-primary hover:text-primary transition-colors">
                {i.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="mb-3 bg-primary text-primary-foreground"><Zap className="h-3 w-3 mr-1" /> Limited launch pricing</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Up to 1 year free. No credit card.</h2>
            <p className="text-muted-foreground">Start free, stay free until you're making money. Then pay less than QuickBooks alone.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl border p-6 flex flex-col ${tier.highlight ? "border-primary shadow-2xl shadow-primary/20 bg-card scale-105 relative" : "border-border bg-card"}`}
              >
                {tier.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">Most Popular</Badge>
                )}
                <div className="text-sm text-muted-foreground">{tier.name}</div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <Badge variant="secondary" className="w-fit mt-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                  {tier.trial}
                </Badge>
                <ul className="space-y-2 mt-5 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={tier.highlight ? "default" : "outline"}
                  onClick={handleGetStarted}
                  className="w-full"
                >
                  Start {tier.trial}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "AutoPilot replied to 47 messages last weekend while I was camping. I made $3,200 without lifting a finger.", name: "Mike R.", role: "Cleaning Service Owner" },
              { quote: "Migrated my entire QuickBooks + Jobber + HubSpot setup in one click. Saving $340/mo and everything's finally in one place.", name: "Sarah L.", role: "Lawn Care Co." },
              { quote: "The prospect map is unfair. I added 18 new clients in my first month just by tapping pins.", name: "Devon K.", role: "Pressure Washing" },
            ].map((t) => (
              <div key={t.name} className="rounded-xl bg-card border p-5">
                <div className="flex gap-0.5 mb-2">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-sm mb-3">"{t.quote}"</p>
                <div className="text-xs">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-muted-foreground">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Replaces strip */}
      <section className="py-14 border-y bg-muted/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-wider text-muted-foreground mb-4">One subscription replaces</p>
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-lg md:text-xl font-semibold text-muted-foreground/80">
            <span>Jobber</span><span>·</span>
            <span>Housecall Pro</span><span>·</span>
            <span>QuickBooks</span><span>·</span>
            <span>Mailchimp</span><span>·</span>
            <span>Calendly</span><span>·</span>
            <span>Gusto</span>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Average customer saves <span className="text-foreground font-semibold">$340/month</span> in software costs.</p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 border-t border-border/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">Loved by operators who hate software</h2>
          <p className="text-center text-muted-foreground mb-12">Real businesses replacing 8+ tools with one platform.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: "Cut our software bill from $487/mo to $79. Invoicing, payroll, and dispatch all in one place. Setup took 20 minutes.",
                name: "Marcus T.",
                role: "Owner, Premier Auto Detail",
                rating: 5,
              },
              {
                quote: "The AI receptionist alone is worth the price. We stopped missing after-hours calls and book 3-4 extra jobs a week from it.",
                name: "Sarah K.",
                role: "GM, BrightHome Cleaning",
                rating: 5,
              },
              {
                quote: "Finally a CRM that doesn't need a 6-week onboarding. My crew was using it day one. Recurring billing alone saved me 5 hours a week.",
                name: "Diego R.",
                role: "Founder, Apex Lawn Care",
                rating: 5,
              },
            ].map((t, i) => (
              <div key={i} className="rounded-xl border border-border/60 bg-card/40 backdrop-blur p-6 space-y-4">
                <div className="flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <span key={j} className="text-primary">★</span>
                  ))}
                </div>
                <p className="text-sm text-foreground/90 leading-relaxed">"{t.quote}"</p>
                <div className="pt-2 border-t border-border/40">
                  <p className="font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-3">
            {[
              { q: "How long is the free trial?", a: "Starter is free for 3 months, Professional for 6 months, and Business for a full year. No credit card required to start." },
              { q: "Can I cancel anytime?", a: "Yes. Cancel from Settings → Billing in one click. No phone calls, no retention scripts, no fees." },
              { q: "Will you migrate my data from Jobber / QuickBooks / HubSpot?", a: "Yes. Use our self-serve Migration Wizard, or book a white-glove concierge migration where we do it for you." },
              { q: "Do I own my data?", a: "Always. Export everything as CSV anytime from Settings → Data. Delete your account and we wipe it within 30 days." },
              { q: "Is my data secure?", a: "Encrypted in transit and at rest, row-level security on every table, daily backups. See our Security page for details." },
              { q: "Does it work on mobile?", a: "Yes — installable PWA that works offline. Clock in, take photos, send invoices from the field." },
              { q: "What if I get stuck?", a: "Email support on every plan. Pro and Business get priority support and onboarding calls." },
              { q: "Do you integrate with Stripe / Twilio / Google?", a: "Yes — native integrations for payments (Stripe Connect), SMS (Twilio), email, calendar, and social DMs." },
              { q: "What happens after the free trial?", a: "Pick a plan or your account pauses (data preserved). We'll never auto-charge you without a heads-up." },
              { q: "Refunds?", a: "Yes — 30 day money-back guarantee on any paid plan, no questions asked." },
            ].map((item) => (
              <details key={item.q} className="group rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold flex items-center justify-between list-none">
                  {item.q}
                  <span className="text-muted-foreground group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-muted/20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Stop running your business.<br />
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Let it run itself.</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">Join the businesses that get back their nights and weekends.</p>
          <Button size="lg" onClick={handleGetStarted} className="group">
            Get started — free for up to 1 year
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-bold mb-3">Detail Pro Suite</div>
            <p className="text-muted-foreground text-xs">All-in-one platform for service businesses.</p>
          </div>
          <div>
            <div className="font-semibold mb-3">Product</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/features" className="hover:text-foreground">Features</a></li>
              <li><a href="/pricing" className="hover:text-foreground">Pricing</a></li>
              <li><a href="/switch-and-save" className="hover:text-foreground">Switch & Save</a></li>
              <li><a href="/migration-concierge" className="hover:text-foreground">Migration concierge</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3">Company</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/about" className="hover:text-foreground">About</a></li>
              <li><a href="/contact" className="hover:text-foreground">Contact</a></li>
              <li><a href="/schedule-demo" className="hover:text-foreground">Book a demo</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3">Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/security" className="hover:text-foreground">Security</a></li>
              <li><a href="/privacy" className="hover:text-foreground">Privacy</a></li>
              <li><a href="/terms" className="hover:text-foreground">Terms</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 mt-8 text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} Detail Pro Suite. All rights reserved.
        </div>
      </footer>


      <TierSelectionDialog
        open={showTierDialog}
        onOpenChange={setShowTierDialog}
        onSelectTier={handleTierSelect}
      />
    </div>
  );
};

export default Landing;
